import { useTranslation } from "react-i18next";
import { DisclosureSection } from "../../ui/DisclosureSection";
import { Tag } from "../../ui/Tag";
import { Tooltip } from "../../ui/Tooltip";
import { ChatTrace } from "../../ui/ChatActionsDropdown";
import { modeDescription, modeShortLabel } from "../../ui/ModePanel";
import type { GptAction } from "../../../domains/gpt/transcript";
import type { GptMode } from "../../../domains/gpt/stores/modeStore";

/** The folding sections of the panel, in display order. */
export type GptContextPanelSection = "progression" | "evidence" | "sources";

/** The engine that answered a turn: `auto` only ever picks one of these. */
export type GptResolvedMode = Exclude<GptMode, "auto">;

/**
 * The mode the panel names: chosen by the reader (or routed by Auto) on the
 * legacy engine, inferred from the question's stakes on the assistant engine,
 * which has no picker (Figma "D3.explore.suivis — survol Mode, nouveau
 * moteur").
 */
export type GptPanelMode =
  | { kind: "chosen"; mode: GptResolvedMode }
  | { kind: "inferred"; mode: "evidence" | "general" };

/** What the turn's research came to, under its Progression. */
export type GptTraceSummary = {
  searchCount: number;
  // Null while the answer streams: its sources arrive with its end.
  sourceCount: number | null;
  // Already localized ("12 s"), or null when the turn was not timed or still streams.
  durationLabel: string | null;
};

/**
 * The desktop conversation's right panel (Figma "UX/UI Desktop Version",
 * "D2.conversation.reponse.progression" "detail.agent"): what grounds the turn
 * being read, where the phone shows it in the Sources and Evidence tabs. It
 * follows the turn the reader has scrolled to, like those tabs. A flat rail on
 * the page canvas, ruled off the conversation.
 *
 * Mode names the engine that answered and explains it on hover.
 * Progression is the turn's trace, Evidence the claim graph of an Evidence
 * turn, Sources the numbered list the answer's citations point at. A section
 * with nothing to show is left out rather than drawn empty.
 *
 * Presentational: the owner builds the section bodies and decides which are
 * open, because the answer itself opens Sources ("9 sources").
 */
export function GptContextPanel({
  mode,
  trace,
  traceSummary,
  evidence,
  sources,
  openSections,
  onToggleSection,
}: {
  // Null on the surfaces with no mode (Chat-with-Paper), and on a turn not
  // yet answered.
  mode: GptPanelMode | null;
  trace: ReadonlyArray<GptAction>;
  traceSummary: GptTraceSummary | null;
  // The Evidence turn's claim graph, or null on any other turn.
  evidence: React.ReactElement | null;
  // Null when the turn has no source to list. Loading spins beside the title
  // while a source is still on its way, as the phone's Sources tab does.
  sources: {
    count: number | null;
    loading: boolean;
    content: React.ReactNode;
  } | null;
  openSections: ReadonlySet<GptContextPanelSection>;
  onToggleSection: (section: GptContextPanelSection) => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="flex h-full flex-col overflow-y-auto border-l border-ds-slate-100 bg-ds-canvas p-6">
      {mode !== null && (
        <section className="flex flex-col items-start gap-2 border-b border-ds-slate-100 pb-3">
          <h2 className="m-0 font-dm-sans text-ui-body font-bold text-ds-slate-950">
            {t("Mode")}
          </h2>
          <Tooltip
            trigger={{
              kind: "inline",
              content: (
                <Tag size="md" variant="blueberry" state="idle" icon={null}>
                  {modeShortLabel(mode.mode, t)}
                </Tag>
              ),
            }}
            side="below"
            content={{ kind: "card", ...modeTooltip(mode, t) }}
          />
        </section>
      )}
      <div className="flex flex-col pt-3">
        {trace.length > 0 && (
          <DisclosureSection
            title={t("Progression")}
            count={null}
            loading={false}
            isOpen={openSections.has("progression")}
            onToggle={() => {
              onToggleSection("progression");
            }}
          >
            <div className="flex flex-col gap-3">
              <ChatTrace
                variant="desktop"
                id={null}
                actions={trace}
                answer={traceSummary === null ? "pending" : "started"}
              />
              {traceSummary !== null && (
                <p className="m-0 font-dm-sans text-xs text-ds-slate-700">
                  {[
                    ...(traceSummary.searchCount > 0
                      ? [
                          t("{{count}} searches", {
                            count: traceSummary.searchCount,
                          }),
                        ]
                      : []),
                    ...(traceSummary.sourceCount === null
                      ? []
                      : [
                          t("{{count}} sources kept", {
                            count: traceSummary.sourceCount,
                          }),
                        ]),
                    ...(traceSummary.durationLabel === null
                      ? []
                      : [traceSummary.durationLabel]),
                  ].join(" · ")}
                </p>
              )}
            </div>
          </DisclosureSection>
        )}
        {evidence !== null && (
          <DisclosureSection
            title={t("Evidence")}
            count={null}
            loading={false}
            isOpen={openSections.has("evidence")}
            onToggle={() => {
              onToggleSection("evidence");
            }}
          >
            {evidence}
          </DisclosureSection>
        )}
        {sources !== null && (
          <DisclosureSection
            title={t("Sources")}
            count={sources.count}
            loading={sources.loading}
            isOpen={openSections.has("sources")}
            onToggle={() => {
              onToggleSection("sources");
            }}
          >
            {sources.content}
          </DisclosureSection>
        )}
      </div>
    </div>
  );
}

// Each call passes a string literal so the i18n scanner can extract it.
function modeTooltip(
  mode: GptPanelMode,
  t: (key: string) => string
): { title: string; body: string } {
  switch (mode.kind) {
    case "chosen":
      return {
        title: modeTitle(mode.mode, t),
        body: modeDescription(mode.mode, t),
      };
    // Not a choice the reader made: the tooltip says why JuisciGPT took it.
    case "inferred": {
      const inferred = mode.mode;
      switch (inferred) {
        case "evidence":
          return {
            title: t("Evidence"),
            body: t(
              "JuisciGPT chose this mode because your question informs a care decision. It read the sources and quotes the exact passages behind each statement."
            ),
          };
        case "general":
          return {
            title: t("General"),
            body: t(
              "JuisciGPT chose this mode because your question does not inform a care decision. It answers from the literature it found while reading your question."
            ),
          };
        default:
          throw new Error(inferred satisfies never);
      }
    }
    default:
      throw new Error(mode satisfies never);
  }
}

function modeTitle(mode: GptResolvedMode, t: (key: string) => string): string {
  switch (mode) {
    case "general":
      return t("General Mode");
    case "evidence":
      return t("Evidence Mode");
    case "find_publication":
      return t("Search Mode");
    default:
      throw new Error(mode satisfies never);
  }
}
